import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { cx } from './cx';

export interface Toast {
  id: number;
  message: string;
  tone: 'neutral' | 'danger';
}

const ToastContext = createContext<((message: string, tone?: Toast['tone']) => void) | null>(null);

export function useToast() {
  const push = useContext(ToastContext);
  if (!push) throw new Error('useToast precisa de <ToastProvider> acima na árvore.');
  return push;
}

/**
 * A região é `polite` e existe desde a carga da página, vazia.
 *
 * Criar o contêiner junto com a mensagem é o erro clássico: o leitor de tela
 * precisa da região no DOM ANTES da mudança para observá-la. O front anterior
 * inseria o toast e o contêiner ao mesmo tempo, e nada era anunciado.
 *
 * `polite` e não `assertive`: confirmação de salvamento não interrompe o que a
 * pessoa está lendo. Erro que exige ação vai em <Notice live> junto do campo,
 * onde o contexto está.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const push = useCallback((message: string, tone: Toast['tone'] = 'neutral') => {
    const id = ++seq.current;
    setToasts((current) => [...current, { id, message, tone }]);
    // 6s: tempo de leitura de uma frase curta com folga. WCAG 2.2.1 pede
    // controle sobre limites de tempo; como nada aqui é acionável e a
    // informação se repete na tela, o descarte automático é aceitável.
    window.setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), 6000);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={cx('toast', toast.tone === 'danger' && 'toast--danger')}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
